import React, { useEffect, useState } from 'react';

interface ImagePreviewProps {
  src?: string | null;
  alt?: string;
  className?: string;
  previewClassName?: string;
  /** Controlled open state. When provided the thumbnail button is hidden. */
  open?: boolean;
  /** Called when the lightbox requests to close (controlled mode). */
  onOpenChange?: (open: boolean) => void;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({
  src,
  alt = 'Preview',
  className = '',
  previewClassName = '',
  open: controlledOpen,
  onOpenChange,
}) => {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const setIsOpen = (v: boolean) => {
    if (isControlled) {
      onOpenChange?.(v);
    } else {
      setInternalOpen(v);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!src) return null;

  return (
    <>
      {!isControlled && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="w-full"
        >
          <img
            src={src}
            alt={alt}
            className={`cursor-zoom-in ${className}`.trim()}
          />
        </button>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <button
            type="button"
            aria-label="Close preview"
            onClick={() => setIsOpen(false)}
            className="absolute inset-0 cursor-zoom-out"
          />
          <div className="relative z-10 max-h-[85vh] max-w-[90vw]">
            <img
              src={src}
              alt={alt}
              className={`max-h-[85vh] max-w-[90vw] rounded-md object-contain shadow-lg ${
                previewClassName || ''
              }`.trim()}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ImagePreview;
